import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { memo, useEffect, useRef, useState } from 'react';

import Box from 'shopper/components/Box';

import LazyHydration from 'components/LazyHydration';

import useIntersectionObserver from 'hooks/useIntersectionObserver';

import { sendEvent } from 'lib/gtag';

import ArticleMainHeading from './ArticleMainHeading';
import ArticleMainText from './MainText';

const ArticleMainFooter = dynamic(() => import('./MainFooter'), {
  ssr: false,
});

const ArticleMain = ({
  articleDescription,
  articleId,
  articleImage,
  articlePublished,
  articleTags = [],
  articleText,
  articleTitle,
  categoryName,
  categorySlug,
  gaEventCategory,
  userDescription,
  userName,
  userPhoto,
  userUsername,
}) => {
  const endOfArticleRef = useRef();
  const [isArticleRead, setArticleRead] = useState(false);

  /**
   * This component does not unmount on page transitions to another
   * article page. We have to reset this state when the article page changes
   */
  useEffect(() => {
    setArticleRead(false);
  }, [articleId]);

  useIntersectionObserver({
    enabled: !isArticleRead,
    target: endOfArticleRef,
    threshold: 0,
    onIntersect: () => {
      setArticleRead(true);
      sendEvent({ action: 'finished_reading', category: gaEventCategory });
    },
  });

  return (
    <Box className="mb-4 w-full p-4 md:p-6">
      <ArticleMainHeading
        articleDescription={articleDescription}
        articleId={articleId}
        articleImage={articleImage}
        articlePublished={articlePublished}
        articleTitle={articleTitle}
        categoryName={categoryName}
        categorySlug={categorySlug}
        gaEventCategory={gaEventCategory}
        userName={userName}
        userPhoto={userPhoto}
        userUsername={userUsername}
      />
      <ArticleMainText
        articleId={articleId}
        articleText={articleText}
        gaEventCategory={gaEventCategory}
      />
      <div ref={endOfArticleRef} />
      <LazyHydration placeholderSize={{ height: 382 }}>
        <ArticleMainFooter
          articleTags={articleTags}
          articleTitle={articleTitle}
          gaEventCategory={gaEventCategory}
          userDescription={userDescription}
          userName={userName}
          userPhoto={userPhoto}
          userUsername={userUsername}
        />
      </LazyHydration>
    </Box>
  );
};

ArticleMain.propTypes = {
  articleId: PropTypes.number.isRequired,
  articleDescription: PropTypes.string.isRequired,
  articleImage: PropTypes.string.isRequired,
  articlePublished: PropTypes.string.isRequired,
  articleTitle: PropTypes.string.isRequired,
  articleTags: PropTypes.arrayOf(
    PropTypes.shape({
      tagId: PropTypes.string.isRequired,
      tagName: PropTypes.string.isRequired,
      tagSlug: PropTypes.string.isRequired,
    })
  ),
  gaEventCategory: PropTypes.string.isRequired,
  userDescription: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
  userUsername: PropTypes.string.isRequired,
  userPhoto: PropTypes.string.isRequired,
};

export default memo(ArticleMain);
